import React, { FC, useMemo, useRef } from 'react';

import { Container, Tab, Tabs } from '@material-ui/core';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import { useTranslation } from 'react-i18next';
import { Link, Redirect, Route, Switch, useLocation, useHistory } from 'react-router-dom';

import { Select } from '../../components/Select';
import { LearnerInfo, Tenant, USER_ROLES } from '../../types';
import { generateRandomImageUrl, translate } from '../../utils/helpers';
import { a11yProps } from '../AnalyticsPage/components/TabPanel/TabPanel';
import { Header } from '../DiscoverPage/components/Header';
import { MyAccountDetailsPage } from './pages/MyAccountDetailsPage';
import { MyOrganisationPage } from './pages/MyOrganisationPage';
import { MySubscriptionPage } from './pages/MySubscriptionPage';
import { MyTeamsPage } from './pages/MyTeamsPage';
import { MY_ACCOUNT_LINKS, getLinksToIndex } from './pages/const';
import useStyles from './styles';

interface MyAccountPageProps {
  tenant?: Tenant;
  userInfo?: LearnerInfo;
  width: Breakpoint;
  authAppId?: string;
}
type SelectOption = {
  display: string;
  value: string;
};

const generateSelectValues = (isOrganizationOwner, isB2B) => {
  const arr: SelectOption[] = [];

  if (isOrganizationOwner) {
    arr.push({
      display: translate('page.account.myOrganization'),
      value: MY_ACCOUNT_LINKS.MyOrganisation,
    });
  }
  if (isB2B) {
    arr.push({
      display: translate('page.account.myTeams'),
      value: MY_ACCOUNT_LINKS.MyTeams,
    });
  }

  return [
    ...arr,
    {
      display: translate('page.account.myAccountDetails'),
      value: MY_ACCOUNT_LINKS.MyAccountDetails,
    },
    {
      display: translate('page.account.mySubscription'),
      value: MY_ACCOUNT_LINKS.MySubscription,
    },
  ];
};

const getIndex = (pathname, values: SelectOption[]) => {
  for (let i = 0; i < values.length; i++) {
    if (pathname.includes(values[i].value)) {
      return i;
    }
  }
  return 0;
};

const MyAccountPage: FC<MyAccountPageProps> = ({ userInfo, width, tenant, authAppId }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const isMobile = !isWidthUp('md', width);
  const chargeBeeSite = userInfo?.sub_provided_by_chargebee_site || tenant?.chargebee_site;

  const selectWrapperRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const pathname =
    location.pathname && location.pathname[location.pathname.length - 1] === '/'
      ? location.pathname.slice(0, -1)
      : location.pathname;
  const history = useHistory();

  const isB2B =
    !!userInfo?.company &&
    (userInfo?.role === USER_ROLES?.CompanyOwner ||
      userInfo?.role === USER_ROLES?.TeamManager ||
      userInfo?.role === USER_ROLES?.B2BLearner);
  const isOrganizationOwner =
    userInfo?.company?.owner_id && userInfo?.id === userInfo.company.owner_id.toString();

  const links = getLinksToIndex(isOrganizationOwner);

  const selectOptions = useMemo(() => {
    return generateSelectValues(isOrganizationOwner, isB2B);
  }, [isOrganizationOwner, isB2B]);

  const index = useMemo(() => {
    return getIndex(pathname, selectOptions);
  }, [pathname, selectOptions]);
  const defaultLink = isOrganizationOwner
    ? MY_ACCOUNT_LINKS.MyOrganisation
    : isB2B
    ? MY_ACCOUNT_LINKS.MyTeams
    : MY_ACCOUNT_LINKS.MyAccountDetails;

  const selectValue = useMemo(() => {
    return selectOptions.find((item) => item.value === links[index]);
  }, [selectOptions, links, index]);

  const headerImage = useMemo(() => generateRandomImageUrl(tenant?.name), [tenant]);

  return (
    <>
      <Header
        wrapperClass={classes.headerWrapper}
        title={t('page.account.title')}
        titleClass={classes.title}
        imageUrl={headerImage}
        tenantName={tenant?.name}
      />
      <Container className={classes.container}>
        {isMobile ? (
          <div ref={selectWrapperRef} className={classes.selectWrapper}>
            <Select
              popperLabel={t('page.account.selectPage')}
              className={classes.select}
              items={selectOptions}
              value={selectValue}
              onChange={(val) => {
                history.push(val?.value);
              }}
              anchorEl={selectWrapperRef?.current}
              selectHeaderClass={classes.selectHeader}
              selectBtnClass={classes.selectButtonBase}
            />
          </div>
        ) : (
          <Tabs
            value={index}
            textColor="inherit"
            classes={{
              root: classes.tabsRoot,
            }}
          >
            {isOrganizationOwner && (
              <Tab
                data-testid="myOrgTab"
                label={t('page.account.myOrganization')}
                {...a11yProps(links.indexOf(MY_ACCOUNT_LINKS.MyOrganisation))}
                component={Link}
                to={MY_ACCOUNT_LINKS.MyOrganisation}
              />
            )}
            {isB2B && (
              <Tab
                data-testid="myTeamsTab"
                label={t('page.account.myTeams')}
                {...a11yProps(links.indexOf(MY_ACCOUNT_LINKS.MyTeams))}
                component={Link}
                to={MY_ACCOUNT_LINKS.MyTeams}
              />
            )}
            <Tab
              data-testid="myAccDetailsTab"
              label={t('page.account.myAccountDetails')}
              {...a11yProps(links.indexOf(MY_ACCOUNT_LINKS.MyAccountDetails))}
              component={Link}
              to={MY_ACCOUNT_LINKS.MyAccountDetails}
            />
            <Tab
              data-testid="mySubTab"
              label={t('page.account.mySubscription')}
              {...a11yProps(links.indexOf(MY_ACCOUNT_LINKS.MySubscription))}
              component={Link}
              to={MY_ACCOUNT_LINKS.MySubscription}
            />
          </Tabs>
        )}

        <Switch>
          <Route
            path={[
              MY_ACCOUNT_LINKS.MyOrganisation,
              MY_ACCOUNT_LINKS.MyAccountDetails,
              MY_ACCOUNT_LINKS.MySubscription,
            ]}
          >
            <div className={classes.card}>
              {isOrganizationOwner && (
                <Route
                  path={MY_ACCOUNT_LINKS.MyOrganisation}
                  render={(props) => (
                    <MyOrganisationPage
                      {...props}
                      tenantTitle={tenant?.title}
                      userInfo={userInfo}
                    />
                  )}
                />
              )}
              <Route
                path={MY_ACCOUNT_LINKS.MyAccountDetails}
                render={(props) => (
                  <MyAccountDetailsPage
                    {...props}
                    tenantTitle={tenant?.title}
                    userInfo={userInfo}
                    authAppId={authAppId}
                  />
                )}
              />
              <Route
                path={MY_ACCOUNT_LINKS.MySubscription}
                render={(props) => (
                  <MySubscriptionPage
                    tenantTitle={tenant?.title}
                    chargeBeeSite={chargeBeeSite}
                    userInfo={userInfo}
                    {...props}
                  />
                )}
              />
            </div>
          </Route>
          {isB2B && (
            <Route
              path={[
                MY_ACCOUNT_LINKS.MyTeams,
                `${MY_ACCOUNT_LINKS.MyTeams}/:id`,
                `${MY_ACCOUNT_LINKS.MyTeams}/:id/invite`,
              ]}
              render={(props) => (
                <MyTeamsPage {...props} tenantTitle={tenant?.title} userInfo={userInfo} />
              )}
            />
          )}
          <Redirect to={defaultLink} />
        </Switch>
      </Container>
    </>
  );
};

export default withWidth()(MyAccountPage);
